import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications, markRead, clearNotifications, pruneOldNotifications, NOTIF_TYPES,
} from '../notifications';
import { timeAgo } from './CommentsSection';
import { pick, notificationText } from '../i18n';


export default function NotificationBell({ name, lang = 'en' }) {
  const t = pick(notificationText, lang);
  const navigate = useNavigate();
  const [items, setItems] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  const load = React.useCallback(() => {
    if (name) fetchNotifications(name).then(setItems);
  }, [name]);

  React.useEffect(() => {
    load();
    if (!name) return;
    // Drop anything read and over a month old, so the table does not grow
    // without bound. Fire and forget: nothing on screen depends on it.
    pruneOldNotifications(name);
    // Cheap poll — this is a small congregation, not a chat app. It stops
    // while the tab is hidden (nobody can see the badge, and it would keep
    // hitting the database all night) and refreshes at once on return.
    let id = null;
    const start = () => { if (id === null) id = setInterval(load, 60000); };
    const stop = () => { if (id !== null) { clearInterval(id); id = null; } };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') stop();
      else { load(); start(); }
    };
    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => { stop(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [name, load]);

  React.useEffect(() => {
    if (!open) return;
    const away = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  if (!name) return null;
  const unread = items.filter(n => !n.read);

  function verb(n) {
    if (n.type === NOTIF_TYPES.MENTION) return t.mention;
    if (n.type === NOTIF_TYPES.REACTION) return `${t.reaction} ${n.emoji || ''}`;
    return t.reply;
  }

  async function openItem(n) {
    setOpen(false);
    if (!n.read) {
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      markRead([n.id]);
    }
    // The comment lives on a reading date; land there and let the page scroll.
    navigate(`/reading?date=${n.date}&comment=${n.comment_id}`);
  }

  async function markAll() {
    const ids = unread.map(n => n.id);
    setItems(prev => prev.map(x => ({ ...x, read: true })));
    markRead(ids);
  }

  async function clearAll() {
    // Unread items disappear too, so ask first rather than silently binning
    // something the reader has not seen.
    if (unread.length > 0 && !window.confirm(t.clearConfirm)) return;
    const previous = items;
    setItems([]);
    setOpen(false);
    try {
      await clearNotifications(name);
    } catch {
      setItems(previous);          // put them back if the delete failed
    }
  }

  return (
    <div className="notif-wrap" ref={ref}>
      <button className="notif-btn" onClick={() => setOpen(o => !o)}
              aria-label={t.title} title={t.title}>
        🔔
        {unread.length > 0 && (
          <span className="notif-badge">{unread.length > 9 ? '9+' : unread.length}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-head">
            <span>{t.title}</span>
            <span className="notif-actions">
              {unread.length > 0 && (
                <button className="notif-markall" onClick={markAll}>{t.markAll}</button>
              )}
              {items.length > 0 && (
                <button className="notif-clear" onClick={clearAll}>{t.clear}</button>
              )}
            </span>
          </div>
          {items.length === 0 && <div className="notif-empty">{t.none}</div>}
          {items.map(n => (
            <button key={n.id} className={`notif-item${n.read ? '' : ' unread'}`}
                    onClick={() => openItem(n)}>
              <div className="notif-line">
                <strong>{n.actor}</strong> {verb(n)}
              </div>
              {n.excerpt && <div className="notif-excerpt">“{n.excerpt}”</div>}
              <div className="notif-time">{n.date} · {timeAgo(n.created_at)}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
